export interface TimelineSubRowItem {
  uid: string;
  onPageStartTime: number;
  timelineSubRow: number;
}

// Tolerance for floating-point comparisons. Times are rounded to 2 decimal places
// (centiseconds), so 1ms is well below the rounding granularity but large enough
// to absorb IEEE-754 drift from arithmetic on those rounded values.
const OVERLAP_EPSILON = 0.001;

function hasOverlapInRow<T extends TimelineSubRowItem>(item: T, row: number, rowMap: Map<number, T[]>, getEndTime: (item: T) => number): boolean {
  return (rowMap.get(row) ?? []).some(
    (existing) =>
      existing.uid !== item.uid &&
      item.onPageStartTime < getEndTime(existing) - OVERLAP_EPSILON &&
      getEndTime(item) > existing.onPageStartTime + OVERLAP_EPSILON
  );
}

/**
 * Re-resolves row assignments after a mutation: overlapping bars on the same row are
 * bumped to the next free row, then used rows are compacted to remove gaps.
 * `prioritizedUids` (bars a gesture just placed deliberately, e.g. a group drop) claim
 * their contested rows first, so bystanders re-rack around them instead of displacing them.
 */
export function recalculateTimelineSubRows<T extends TimelineSubRowItem>(items: T[], getEndTime: (item: T) => number, prioritizedUids: readonly string[] = []): void {
  if (items.length === 0) {
    return;
  }

  const prioritized = new Set(prioritizedUids);
  const sorted = [...items].sort((a, b) => {
    if (a.timelineSubRow !== b.timelineSubRow) {
      return a.timelineSubRow - b.timelineSubRow;
    }
    if (prioritized.has(a.uid) !== prioritized.has(b.uid)) {
      return prioritized.has(a.uid) ? -1 : 1;
    }
    return a.onPageStartTime - b.onPageStartTime;
  });

  const rowMap = new Map<number, T[]>();

  for (const item of sorted) {
    let assignedRow = item.timelineSubRow;

    if (hasOverlapInRow(item, assignedRow, rowMap, getEndTime)) {
      assignedRow = 0;
      while (hasOverlapInRow(item, assignedRow, rowMap, getEndTime)) {
        assignedRow += 1;
      }
    }

    item.timelineSubRow = assignedRow;
    const rowItems = rowMap.get(assignedRow) ?? [];
    rowItems.push(item);
    rowMap.set(assignedRow, rowItems);
  }

  // Compact: remap used rows to eliminate gaps
  const usedRows = [...new Set(items.map((item) => item.timelineSubRow))].sort((a, b) => a - b);
  const rowMapping = new Map<number, number>();
  usedRows.forEach((row, index) => {
    rowMapping.set(row, index);
  });

  for (const item of items) {
    item.timelineSubRow = rowMapping.get(item.timelineSubRow) ?? 0;
  }
}
